import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  Stack,
  Pagination,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../components/TableItem";
import InputField from "../../FormComponents/InputField";
import Alart from "../../components/Alart";
import { getRoutesByPaging } from "../../services/Route";
// import AddRoute from "./AddRoute";

// Icons
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RouteIcon from "@mui/icons-material/Route";
import SearchIcon from "@mui/icons-material/Search";
import { RoleBasedRender } from "../../components/RoleBaseRender";
import PersonIcon from "@mui/icons-material/Person";
import SalesmanSelectingPopup from "../../components/SalesmanSelectingPopup";
import CustomerSelectingPopup from "../../components/CustomerSelectingPopup";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  backgroundColor: "#f9fafb",
}));

const HeaderCard = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: "none",
}));

const TableContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.05)",
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginTop: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 1.5,
  },
}));

const CountChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

let _routeListState = null;

function Routes() {
  const theme = useTheme();
  const history = useHistory();

  // State variables
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openSalesPersonSelection, setOpenSalesPersonSelection] =
    useState(false);
  const [openCustomerSelection, setOpenCustomerSelection] = useState(false);
  const [selectedSalesman, setSelectedSalesman] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState([]);

  // Disable route warning state
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Are you sure you want to disable this route?",
  });
  const [disableItemId, setDisableItemId] = useState(null);

  // Filter state
  const [filter, setFilter] = useState({
    _routeId: "",
    _routeName: "",
    _salespersonIds: [],
    _customerIds: [],
  });

  // Table columns definition
  const columns = [
    { id: "routeId", label: "Route ID", minWidth: 120 },
    { id: "routeName", label: "Route Name", minWidth: 200 },
    {
      id: "customerCount",
      label: "Customers",
      minWidth: 120,
      format: (value) => (
        <CountChip
          label={value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      id: "salespersonCount",
      label: "Salespersons",
      minWidth: 140,
      format: (value) => (
        <CountChip
          label={value}
          size="small"
          color="success"
          variant="outlined"
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 150 },
  ];

  // Init data fetch
  useEffect(() => {
    if (_routeListState) {
      const { page: savedPage, filter: savedFilter } = _routeListState;
      _routeListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Data creation helper
  function createData(
    routeId,
    routeName,
    customerCount,
    salespersonCount,
    action
  ) {
    return { routeId, routeName, customerCount, salespersonCount, action };
  }

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Main data fetch function
  const fetchData = async (routeFilter, pageNo) => {
    setIsLoading(true);
    try {
      const result = await getRoutesByPaging(pageNo, rowsPerPage, routeFilter);
      const routeSet = result.content;

      // Set pagination info
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      // Format data for table
      const newSet = [];
      if (routeSet && routeSet.length > 0) {
        for (let x = 0; x < routeSet.length; x++) {
          const route = routeSet[x];
          newSet.push(
            createData(
              route.routeId,
              route.routeName,
              route.customers ? route.customers.length : 0,
              route.salespersons ? route.salespersons.length : 0,
              route.routeId
            )
          );
        }
      }

      setRows(newSet);
    } catch (error) {
      console.error("Error fetching route data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Action handler for table rows
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _routeListState = { page, filter };
        history.push("/template/route_view/" + id);
        break;
      case "edit":
        history.push("/template/update_route/" + id);
        break;
      case "disable":
        disableRoute(id);
        break;
      default:
        return;
    }
  };

  // Disable route handler
  const disableRoute = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Action buttons for disable warning
  function DisableWarningActionButtons() {
    return (
      <>
        <ActionButton
          variant="contained"
          color="error"
          onClick={async () => {
            // await DeactivateRouteById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData(filter, page);
          }}
        >
          Disable
        </ActionButton>
        <ActionButton
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </ActionButton>
      </>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _routeId: "",
      _routeName: "",
      _salespersonIds: [],
      _customerIds: [],
    });

    setSelectedSalesman([]);
    setSelectedCustomer([]);
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // If form is open, show AddRoute component
  if (openForm) {
    return (
      <StyledPaper elevation={3}>
        {/* <AddRoute setOpenForm={setOpenForm} /> */}
      </StyledPaper>
    );
  }

  // Salesman selection popup handlers
  const handleOpenSalesmanSelection = () => {
    setOpenSalesPersonSelection(true);
  };

  const handleCloseSalesmanSelection = () => {
    setOpenSalesPersonSelection(false);
    setFilter({
      ...filter,
      _salespersonIds: selectedSalesman,
    });
  };

  const handleOpenCustomerSelection = () => {
    setOpenCustomerSelection(!openCustomerSelection);
    if (openCustomerSelection) {
      setFilter({
        ...filter,
        _customerIds: selectedCustomer,
      });
    }
  };

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />

      <StyledPaper elevation={3}>
        {/* Header */}
        <HeaderCard>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <RouteIcon fontSize="large" color="primary" />
            <Box>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Route Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage sales routes and assignments
              </Typography>
            </Box>
          </Box>
          <RoleBasedRender allowedRoles={["ROUTE_CREATE", "ROLE_ADMIN"]}>
            <ActionButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenForm(true)}
            >
              Add Route
            </ActionButton>
          </RoleBasedRender>
        </HeaderCard>

        <Divider sx={{ mb: 3 }} />

        {/* Search and filter controls */}
        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <StyledTextField
            placeholder="Search by route name..."
            variant="outlined"
            size="small"
            sx={{ width: "40%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              handleFilterChange("_routeName", e.target.value);
              if (e.target.value.length > 2 || e.target.value.length === 0) {
                fetchData({ ...filter, _routeName: e.target.value }, page);
              }
            }}
          />
          <Stack direction="row" spacing={2}>
            <Tooltip title="Toggle Filters">
              <ActionButton
                variant="outlined"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={toggleFilters}
              >
                Filters
              </ActionButton>
            </Tooltip>
            <Chip
              label={`${totalRows} Routes`}
              variant="outlined"
              color="primary"
            />
          </Stack>
        </Box>

        {/* Expanded filters */}
        {showFilters && (
          <FilterContainer>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_routeId"
                    value={filter._routeId}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Route ID"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_routeName"
                    value={filter._routeName}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Route Name"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <SelectButton
                    startIcon={<PersonIcon />}
                    onClick={handleOpenSalesmanSelection}
                    endIcon={
                      selectedSalesman.length > 0 ? (
                        <Chip size="small" label={selectedSalesman.length} />
                      ) : null
                    }
                  >
                    {selectedSalesman.length > 0
                      ? "Salesmen Selected"
                      : "Select Salesman"}
                  </SelectButton>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <SelectButton
                    startIcon={<PersonIcon />}
                    onClick={handleOpenCustomerSelection}
                    endIcon={
                      selectedCustomer.length > 0 ? (
                        <Chip size="small" label={selectedCustomer.length} />
                      ) : null
                    }
                  >
                    {selectedCustomer.length > 0
                      ? "Customer Selected"
                      : "Select Customer"}
                  </SelectButton>
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={() => {
                        resetFilters();
                        fetchData({}, page);
                      }}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={() => fetchData(filter, page)}
                    >
                      Search
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </CardContent>
          </FilterContainer>
        )}

        {/* Route Table */}
        <TableContainer elevation={1}>
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "500px",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography color="text.secondary">
                  Loading routes...
                </Typography>
              </Box>
            ) : rows.length > 0 ? (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Route view",
                  edit: "Route edit",
                  disable: "Route Deactivate",
                }}
                actionRoles={{
                  view: ["ROUTE_VIEW", "ROLE_ADMIN"],
                  edit: ["ROUTE_UPDATE", "ROLE_ADMIN"],
                  disable: ["ROUTE_DISABLE", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                }}
              >
                <RouteIcon
                  sx={{
                    fontSize: 60,
                    color: alpha(theme.palette.text.secondary, 0.2),
                    mb: 1,
                  }}
                />
                <Typography variant="h6" color="text.secondary">
                  No routes found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Try adjusting your search filters or add a new route
                </Typography>
              </Box>
            )}
          </CardContent>
        </TableContainer>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <PaginationContainer>
            <Pagination
              count={totalPages}
              page={page + 1}
              variant="outlined"
              shape="rounded"
              color="primary"
              onChange={handleChange}
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: theme.shape.borderRadius,
                },
              }}
            />
          </PaginationContainer>
        )}

        {openSalesPersonSelection && (
          <SalesmanSelectingPopup
            setOpenSalesmanTable={setOpenSalesPersonSelection}
            openSalesmanTable={openSalesPersonSelection}
            handleCloseSalesmanTable={handleCloseSalesmanSelection}
            setSelectedSalesman={setSelectedSalesman}
            selectedSalesman={selectedSalesman}
            isOneChoise={false}
          />
        )}

        {openCustomerSelection && (
          <CustomerSelectingPopup
            setOpenCustomerTable={setOpenCustomerSelection}
            openCustomerTable={openCustomerSelection}
            handleCloseCustomerTable={handleOpenCustomerSelection}
            setSelectedCustomer={setSelectedCustomer}
            selectedCustomer={selectedCustomer}
            isOneChoise={true}
          />
        )}
      </StyledPaper>
    </>
  );
}

export default Routes;
